import { Avatar, Card, CardActionArea, CardActions, Grid, Theme } from '@mui/material';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useMediaApplicationContext } from '../../../components/MediaApplicationContext';
import OverflowTooltip from '../../../components/OverflowTooltip';
import { myNetworkCardAspectRatioPadding } from '../../../components/useMediaStyles';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import useTeceoStyles from '../../../useTeceoStyles';
import { getColorByRole, prevent } from '../../../utils/generics';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../../login/AuthContext';
import MediaRenderer from '../../media/MediaRenderer';
import { useTranslation } from '../../translation/TranslationContext';
import ConnectionActionButton from '../connections/ConnectionActionButton';
import { getCardDescription } from '../myNetworkUtils';
import { Explore } from './myNetworkExplore.types';

type MyNetWorkExploreCardProps = {
  connection: Explore;
  reload: () => void;
  updateNotifications: () => void;
  showConnectionButton?: boolean;
  shouldVerifyStorefront?: boolean;
  hideLink?: boolean;
  redirectUrl?: string;
  shouldGetDescriptionForBrand?: boolean;
  beforeRedirect?: (onSuccess: () => void) => Promise<void>;
};

const useStyles = makeStyles()((theme: Theme) => ({
  card: {
    height: '100%',
    borderRadius: theme.shape.borderRadius,
  },
  avatar: {
    width: theme.spacing(9 / 2),
    height: theme.spacing(9 / 2),
    textTransform: 'uppercase',
  },
  notAllowedCursor: {
    '&:hover': {
      cursor: 'not-allowed',
    },
    paddingTop: myNetworkCardAspectRatioPadding,
  },
  media: {
    paddingTop: myNetworkCardAspectRatioPadding,
  },
  address: {
    color: theme.palette.text.secondary,
  },
  slogan: {
    maxWidth: 400,
  },
  cardActions: {
    padding: theme.spacing(3 / 2),
  },
}));

const MyNetWorkExploreCard = ({
  connection,
  reload,
  updateNotifications,
  shouldGetDescriptionForBrand = true,
  showConnectionButton = true,
  hideLink,
  redirectUrl,
  beforeRedirect,
}: MyNetWorkExploreCardProps) => {
  const { loggedin, handleSetLogin } = useAuthContext();
  const { classes } = useStyles();
  const { classes: teceoClasses } = useTeceoStyles();
  const mediaContext = useMediaApplicationContext();
  const { isBrand } = useApplicationContext();
  const location = useLocation();
  const history = useHistory();
  const { handleMessage } = snackbarStore;

  const onChangeConnection = () => {
    reload();
    updateNotifications();
  };

  const {
    logoUrl,
    slug,
    name,
    title,
    bannerUrl,
    addresses,
    enablePublicShowroom,
    id,
    customerSupportPhone,
  } = connection.brand ? connection.brand : connection.store;
  const slugOrEmpty = redirectUrl ?? `/${slug || location.pathname.slice(1)}`;

  const getBackgroundColor = () => {
    if (logoUrl) {
      return 'transparent';
    }
    return connection.store
      ? getColorByRole({ userRole: 'ADMIN_LOJA' })
      : getColorByRole({ userRole: 'ADMIN_MARCA' });
  };

  const { translate } = useTranslation();

  const handleClickOnLink = async (e: React.SyntheticEvent<HTMLAnchorElement, MouseEvent>) => {
    if (beforeRedirect) {
      prevent(e);
      await beforeRedirect(() => history.push(slugOrEmpty));
    }
  };

  const handleRedirectToShowroom = () => {
    handleSetLogin({
      id,
      slug,
      bannerUrl: bannerUrl || '',
      enablePublicShowroom,
      brandName: name,
      customerSupportPhone,
    });
    history.push(slugOrEmpty);
  };

  const handleClick = () => {
    if (!slug) {
      handleMessage({
        message: connection.brand
          ? translate(LangEnum.SNACKBAR_BRAND_DONT_HAVE_PAGE_YET)
          : translate(LangEnum.SNACKBAR_STORE_DONT_HAVE_PAGE_YET),
        type: 'warning',
      });
    }
  };

  const description = getCardDescription(
    !!shouldGetDescriptionForBrand && isBrand,
    addresses,
    title,
  );

  const renderPublicMediaCard = () =>
    !enablePublicShowroom ? (
      <MediaRenderer
        src={mediaContext.getMediaFromSize(bannerUrl, 'sm')}
        type="IMAGE"
        className={classes.notAllowedCursor}
      />
    ) : (
      <CardActionArea onClick={handleRedirectToShowroom}>
        <MediaRenderer
          src={mediaContext.getMediaFromSize(bannerUrl, 'sm')}
          type="IMAGE"
          className={classes.media}
        />
      </CardActionArea>
    );

  const renderMediaCard = () =>
    loggedin ? (
      <Link to={slugOrEmpty} className={teceoClasses.linkDisguised} onClick={handleClickOnLink}>
        <MediaRenderer
          src={mediaContext.getMediaFromSize(bannerUrl, 'sm')}
          type="IMAGE"
          className={classes.media}
        />
      </Link>
    ) : (
      renderPublicMediaCard()
    );

  return (
    <Card onClick={handleClick} className={classes.card} elevation={0}>
      {hideLink ? (
        <CardActionArea>
          <MediaRenderer
            src={mediaContext.getMediaFromSize(bannerUrl, 'sm')}
            type="IMAGE"
            className={classes.media}
          />
        </CardActionArea>
      ) : (
        renderMediaCard()
      )}
      <CardActions className={classes.cardActions}>
        <Grid
          wrap="nowrap"
          container
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          size="grow"
        >
          <Grid
            size={{
              xs: !showConnectionButton ? 'grow' : 6,
              sm: !showConnectionButton ? 'grow' : 7,
            }}
          >
            <Grid
              container
              wrap="nowrap"
              spacing={1}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid>
                <Avatar
                  className={classes.avatar}
                  style={{
                    backgroundColor: getBackgroundColor(),
                  }}
                  src={logoUrl}
                >
                  {name[0]}
                </Avatar>
              </Grid>
              <Grid
                size={{
                  xs: 8,
                  md: 9,
                  lg: 10,
                }}
              >
                <OverflowTooltip className={teceoClasses.boldText} text={name}>
                  {name}
                </OverflowTooltip>
                <OverflowTooltip text={description} className={classes.slogan}>
                  {description}
                </OverflowTooltip>
              </Grid>
            </Grid>
          </Grid>
          {showConnectionButton &&
            (loggedin ? (
              <Grid>
                <ConnectionActionButton
                  connection={connection}
                  brandSlug={slug}
                  onChangeConnection={onChangeConnection}
                  shouldVerifyStorefront={false}
                  acceptUnverifiedStoreInvite={true}
                />
              </Grid>
            ) : (
              <Grid>
                <ConnectionActionButton
                  connection={connection}
                  brandSlug={slug}
                  onChangeConnection={() => {
                    return;
                  }}
                />
              </Grid>
            ))}
        </Grid>
      </CardActions>
    </Card>
  );
};

export default MyNetWorkExploreCard;
